import { useState, useEffect } from "react";
import Login from "./Login";
import Dashboard from "./Dashboard";
import InventoryDashboard from "./InventoryDashboard";
import EmployeeManagement from "./components/EmployeeManagement";
import KioskSetup from "./KioskSetup";
import ToastContainer from "./components/Toast";
import ResetPassword from "./ResetPassword";
import API_URL from "./config";
import getDeviceId from "./utils/device";

// Signed-in screens drop back to the passcode screen after this long with no
// touches, so the next person can't act under the previous employee's name.
const IDLE_LOCK_MS = 3 * 60 * 1000;

export default function App() {
  const [empSession, setEmpSession] = useState(null);
  const [view, setView] = useState("kiosk");
  const [deviceReady, setDeviceReady] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [offline, setOffline] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("reset");

  const devBypass = process.env.REACT_APP_DEV_BYPASS === "true";

  const checkStatus = () => {
    fetch(`${API_URL}/api/kiosk/status?device_id=${getDeviceId()}`)
      .then(r => {
        if (!r.ok) throw new Error(`status ${r.status}`);
        return r.json();
      })
      .then(d => {
        setOffline(false);
        setDeviceReady(d.registered);
        if (d.business_name) setBusinessName(d.business_name);
      })
      // Network down or server error is NOT "unregistered" — showing the
      // setup screen here would ask staff to re-register a working kiosk.
      // Keep retrying until the server gives a real answer.
      .catch(() => {
        setOffline(true);
        setTimeout(checkStatus, 5000);
      });
  };

  useEffect(() => {
    if (devBypass) {
      fetch(`${API_URL}/api/dev/bypass`).then(checkStatus).catch(checkStatus);
    } else {
      checkStatus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!devBypass || deviceReady !== true || empSession) return;
    fetch(`${API_URL}/api/dev/bypass`)
      .then(r => r.json())
      .then(d => {
        const k = d.kiosk;
        setEmpSession({ token: k.token, role: k.role, name: k.name });
        setView("inventory");
      })
      .catch(() => {});
  }, [devBypass, deviceReady, empSession]);

  useEffect(() => {
    if (!empSession) return;
    let timer;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { setEmpSession(null); setView("kiosk"); }, IDLE_LOCK_MS);
    };
    const events = ["pointerdown", "keydown", "wheel", "touchmove"];
    events.forEach(ev => window.addEventListener(ev, arm, { passive: true }));
    arm();
    return () => {
      clearTimeout(timer);
      events.forEach(ev => window.removeEventListener(ev, arm));
    };
  }, [empSession]);

  if (resetToken) {
    return (
      <>
        <ToastContainer />
        <ResetPassword
          token={resetToken}
          onDone={() => {
            window.history.replaceState({}, "", window.location.pathname);
            window.location.reload();
          }}
        />
      </>
    );
  }

  if (deviceReady === null) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <p className="text-slate-400 text-sm">
        {offline ? "Can't reach server — retrying…" : "Loading…"}
      </p>
    </div>
  );

  if (!deviceReady) return (
    <>
      <ToastContainer />
      <KioskSetup onSetupComplete={() => setDeviceReady(true)} />
    </>
  );

  const reset = () => { setEmpSession(null); setView("kiosk"); };

  return (
    <>
      <ToastContainer />
      {view === "kiosk" && (
        <Login
          businessName={businessName}
          onSuccess={(token, role, name, destination) => {
            setEmpSession({ token, role, name });
            setView(destination);
          }}
        />
      )}
      {view === "attendance" && (
        <Dashboard token={empSession?.token} role={empSession?.role} onDone={reset} />
      )}
      {view === "inventory" && (
        <InventoryDashboard
          token={empSession?.token}
          role={empSession?.role}
          inventoryUser={empSession}
          onDone={reset}
          onAttendance={() => setView("attendance")}
        />
      )}
      {view === "employees" && (
        <EmployeeManagement token={empSession?.token} role={empSession?.role} onDone={reset} />
      )}
    </>
  );
}
